/** The two fields of a JSON Patch op this reads: where it lands, and what it does there. */
export type PatchOp = { op: string; path: string };

const STUDIES_ROOT = '/studies';

/** The id of the study an op lands in, or `undefined` for an op outside the study tree or on the map itself. */
function studyIdOf(op: PatchOp): string | undefined {
  return op.path.startsWith(`${STUDIES_ROOT}/`) ? op.path.slice(STUDIES_ROOT.length + 1).split('/')[0] : undefined;
}

/** Whether the op lands anywhere in the project's study tree. */
export function isStudyOp(op: PatchOp): boolean {
  return op.path === STUDIES_ROOT || op.path.startsWith(`${STUDIES_ROOT}/`);
}

/**
 * The studies a patch touches, by id — or `null` when an op lands on the `/studies` map itself, which
 * means every study the reader holds.
 */
export function touchedStudyIds(ops: readonly PatchOp[]): string[] | null {
  const ids = new Set<string>();
  for (const op of ops) {
    if (op.path === STUDIES_ROOT) {
      return null;
    }
    const studyId = studyIdOf(op);
    if (studyId) {
      ids.add(studyId);
    }
  }
  return [...ids];
}

/** The studies a patch removes outright (`remove /studies/{id}`). */
export function removedStudyIds(ops: readonly PatchOp[]): string[] {
  return ops
    .filter(op => op.op === 'remove' && studyIdOf(op) !== undefined && op.path === `${STUDIES_ROOT}/${studyIdOf(op)}`)
    .map(op => studyIdOf(op) as string);
}

/**
 * What a project patch means for one study on screen: the study is gone, or something in it changed —
 * and whether that something is what the grid draws its rows from.
 */
export type StudyChange = { kind: 'deleted' } | { kind: 'changed'; rows: boolean };

/**
 * Reads a patch as one study's change, or `null` when the patch does not touch that study. Paths are
 * root-relative on the project object the backend diffs whole (`JsonDiffAspect.java`), so a study's own
 * subtree is `/studies/{studyId}`.
 *
 * `rows` is deliberately narrow: only what the grid's rows are drawn from — the subcategories (their
 * units, working conditions, counts) and the currency the prices are formatted in. A rename, a note edit
 * or the `updated` stamp that rides along with every write must not re-query the grid, because
 * `refreshServerSide({ purge: true })` sends the user back to page 1 (`PLAN.md` OQ 9).
 */
export function readStudyChange(ops: readonly PatchOp[], studyId: string): StudyChange | null {
  const root = `/studies/${studyId}`;
  const scoped = ops.filter(op => op.path === root || op.path.startsWith(`${root}/`));
  if (scoped.length === 0) {
    return null;
  }

  if (scoped.some(op => op.op === 'remove' && op.path === root)) {
    return { kind: 'deleted' };
  }

  return {
    kind: 'changed',
    rows: scoped.some(op => op.path.startsWith(`${root}/subcategories`) || op.path === `${root}/currencyCode`),
  };
}
